import angularModule from 'Lessons/angularModule/scripts/lessons_module';
import template from 'Lessons/angularModule/views/stream/student_dashboard_playlists_box.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { setupStyleHelpers, setupBrandNameProperties } from 'AppBranding';
import { type IRootScopeService, type IScope } from 'angular';
import { getIsCurrentOrHasCompletedActiveProgram, getUserProgramState, type CurrentUserIguanaObject } from 'Users';
import { type PlaylistIguanaObject, type PlaylistClass, playlistToIguanaAttrs, deriveActivePlaylist } from 'Playlists';
import { type PublishedPlaylist } from 'FrontRoyalStore';
import { type CohortIguanaObject, type CohortClass } from 'Cohorts';
import { type StudentDashboardContentResponse } from 'StudentDashboard';
import { streamToIguanaAttrs } from 'Lessons/stream';
import { type UserProgramState } from 'ProgramAoi';
import { type SafeApply } from 'SafeApply';
import { type AppHeaderViewModel } from 'NavigationHelpers';
import { type useContentProgressResponse } from 'ContentProgress';
import { shoveProgressIntoContent } from 'LessonProgress';
import {
    type LessonIguanaObject,
    type ContentAccessHelperClass,
    type StreamIguanaClass,
    type StreamIguanaObject,
} from '../../../Lessons.types';

type Scope = IScope & {
    brandStyleClass: string;
    currentUser: CurrentUserIguanaObject;
    relevantCohort: CohortIguanaObject;
    playlists: PlaylistIguanaObject[];
    streams: StreamIguanaObject[];
    activePlaylist: PlaylistIguanaObject | null;
    activatePlaylist: (playlist: PlaylistIguanaObject) => void;
    recommendedPlaylist: PlaylistIguanaObject | null;
    defaultPlaylistIcon: PlaylistClass['DEFAULT_ICON'];
    concentrationPlaylists: (PublishedPlaylist | PlaylistIguanaObject)[];
    numUnlockedConcentrationPlaylists: number;
    switchLearningBoxMode: (mode: 'playlists' | 'active_playlist') => void;
    studentDashboardContent: StudentDashboardContentResponse;
    prefUps: UserProgramState;
    keepLearningStream: StreamIguanaObject | null;
    readyToShowPlaylistMap: boolean;
    keepLearningLesson: LessonIguanaObject | null;
    modifyHeaderSection: boolean;
    playlistsPercentComplete: number;
    playlistReferencesNeedUpdatingAfterProgressUpdate?: boolean;
};

type RootScope = IRootScopeService & {
    currentUser: CurrentUserIguanaObject;
};

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('studentDashboardPlaylistsBox', [
    '$injector',

    function factory($injector: ng.auto.IInjectorService) {
        const $rootScope = $injector.get<RootScope>('$rootScope');
        const Playlist = $injector.get<PlaylistClass>('Playlist');
        const Cohort = $injector.get<CohortClass>('Cohort');
        const Stream = $injector.get<StreamIguanaClass>('Lesson.Stream');
        const ContentAccessHelper = $injector.get<ContentAccessHelperClass>('ContentAccessHelper');
        const safeApply = $injector.get<SafeApply>('safeApply');
        const AppHeaderViewModel = $injector.get<AppHeaderViewModel>('Navigation.AppHeader.AppHeaderViewModel');
        const $timeout = $injector.get<ng.ITimeoutService>('$timeout');

        return {
            restrict: 'E',
            templateUrl,
            scope: {
                switchLearningBoxMode: '<',
                studentDashboardContent: '<?',
                contentProgress: '<?',
                modifyHeaderSection: '<',
            },
            link(scope: Scope) {
                scope.defaultPlaylistIcon = Playlist.DEFAULT_ICON;
                scope.readyToShowPlaylistMap = false;
                scope.playlistReferencesNeedUpdatingAfterProgressUpdate = scope.modifyHeaderSection;

                Object.defineProperty(scope, 'prefUps', {
                    get() {
                        return getUserProgramState(scope.currentUser);
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'currentUser', {
                    get() {
                        return $rootScope.currentUser;
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'relevantCohort', {
                    get() {
                        return scope.currentUser?.relevantCohort;
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'showCompletePlaylistsHeader', {
                    get() {
                        if (scope.modifyHeaderSection) return false;

                        return !!(
                            scope.activePlaylist?.complete &&
                            scope.recommendedPlaylist &&
                            scope.activePlaylist !== scope.recommendedPlaylist
                        );
                    },
                });

                scope.$watch('playlists', (playlists: PlaylistIguanaObject[]) => {
                    if (!playlists || playlists.length === 0) {
                        scope.playlistsPercentComplete = 0;
                        return;
                    }

                    scope.playlistsPercentComplete =
                        scope.relevantCohort?.getSelectedCoursesPercentComplete(playlists) || 0;
                });

                Object.defineProperty(scope, 'playlistsViewDescriptionLocaleKey', {
                    get() {
                        // FIXME: seems like the relevantCohort?.learningBoxDescriptionKey should
                        // be unnecessary, but MiyaMiya users have the "external" programType for some reason
                        return (
                            scope.relevantCohort?.learningBoxDescriptionKey ||
                            Cohort.learningBoxDescriptionKey(scope.currentUser?.programType) ||
                            'description_playlists'
                        );
                    },
                });

                Object.defineProperty(scope, 'playlistsViewTitleKey', {
                    get() {
                        // FIXME: seems like the relevantCohort?.learningBoxDescriptionKey should
                        // be unnecessary, but MiyaMiya users have the "external" programType for some reason
                        return (
                            scope.relevantCohort?.learningBoxTitleKey ||
                            Cohort.learningBoxTitleKey(scope.currentUser?.programType) ||
                            'playlists'
                        );
                    },
                });

                Object.defineProperty(scope, 'programTitle', {
                    get() {
                        return scope.relevantCohort?.standaloneShortProgramTitle;
                    },
                });

                function updateActivePlaylistForLocalePackId() {
                    scope.activePlaylist = deriveActivePlaylist(scope.playlists, scope.currentUser);
                }

                scope.$watchGroup(
                    ['studentDashboardContent', 'contentProgress', 'readyToShowPlaylistMap'],
                    async ([content, progress, readyToShowPlaylistMap]: [
                        StudentDashboardContentResponse,
                        useContentProgressResponse,
                        boolean,
                    ]) => {
                        if (!content || !progress) return;
                        if (readyToShowPlaylistMap) return;
                        scope.recommendedPlaylist = null;
                        const { streamProgressByLocalePackId, lessonProgressByLocalePackId } = progress;

                        scope.streams = content.lessonStreams.map(stream => {
                            const streamProgress = streamProgressByLocalePackId[stream.localePack.id];

                            const cachedStream = Stream.getCachedForLocalePackId(stream.localePack.id);

                            if (cachedStream) {
                                return cachedStream;
                            }
                            return Stream.new(
                                streamToIguanaAttrs(stream, streamProgress, lessonProgressByLocalePackId),
                            );
                        });

                        scope.playlists = content.availablePlaylists.map(playlist => {
                            const cachedPlaylist = Playlist.getCachedForLocalePackId(playlist.localePack.id);

                            if (cachedPlaylist) {
                                return cachedPlaylist;
                            }
                            return Playlist.new(playlistToIguanaAttrs(playlist));
                        });

                        updateActivePlaylistForLocalePackId();

                        scope.concentrationPlaylists = scope.relevantCohort
                            ? scope.relevantCohort.getConcentrationPlaylists(scope.playlists)
                            : scope.playlists;

                        scope.numUnlockedConcentrationPlaylists = scope.concentrationPlaylists.filter(p =>
                            ContentAccessHelper.canLaunch(p as PlaylistIguanaObject),
                        ).length;

                        const hasSinglePlaylist = scope.playlists && scope.playlists.length === 1;

                        if (hasSinglePlaylist && AppHeaderViewModel.learningBoxMode) {
                            AppHeaderViewModel.learningBoxMode.hasSinglePlaylist = true;
                        }
                        scope.readyToShowPlaylistMap = true;

                        $timeout(async () => {
                            // Force progress to be updated on the streams and their references on the playlists
                            // to ensure that the recommended playlist and percent complete are correct
                            await shoveProgressIntoContent(scope.streams, scope.currentUser);

                            if (scope.playlistReferencesNeedUpdatingAfterProgressUpdate) {
                                scope.playlists.forEach(playlist => {
                                    if (!playlist.streamLocalePackIds) return;

                                    const updatedStreams = scope.streams.filter(s =>
                                        playlist.streamLocalePackIds.includes(s.localePackId),
                                    );

                                    playlist.stream_entries = updatedStreams.map(stream => ({
                                        stream,
                                        available_stream: stream,
                                        locale_pack_id: stream.localePackId,
                                    }));
                                });
                            }

                            scope.playlists = [...scope.playlists];
                            safeApply(scope);

                            if (scope.activePlaylist && scope.activePlaylist.complete) {
                                const specialization_lpids = scope.relevantCohort
                                    ? scope.relevantCohort.specialization_playlist_pack_ids || []
                                    : [];

                                // Don't recommend complete nor specialization playlists (the user chooses those)
                                let recommendablePlaylists = scope.playlists.filter(p => {
                                    if (p.complete) return false;
                                    if (specialization_lpids.includes(p.localePackId)) return false;
                                    return ContentAccessHelper.canLaunch(p);
                                });

                                if (scope.relevantCohort) {
                                    // Do not recommend the Business Foundations playlist to
                                    // accepted users in a cohort that should exclude the
                                    // foundations playlist on acceptance.
                                    if (
                                        getIsCurrentOrHasCompletedActiveProgram(scope.currentUser) &&
                                        Cohort.excludesFoundationsPlaylistOnAcceptance(scope.currentUser.programType)
                                    ) {
                                        const foundationsPlaylist = recommendablePlaylists.find(
                                            playlist =>
                                                playlist.localePackId ===
                                                scope.relevantCohort.foundationsPlaylistLocalePackId,
                                        );
                                        if (foundationsPlaylist) {
                                            recommendablePlaylists = recommendablePlaylists.filter(
                                                p => p.localePackId !== foundationsPlaylist.localePackId,
                                            );
                                        }
                                    }

                                    // use the playlist order defined by the cohort
                                    scope.recommendedPlaylist = [...recommendablePlaylists].sort((a, b) => {
                                        const indexA = scope.relevantCohort.concentrationPlaylistPackIds.indexOf(
                                            a.localePackId,
                                        );
                                        const indexB = scope.relevantCohort.concentrationPlaylistPackIds.indexOf(
                                            b.localePackId,
                                        );
                                        const safeIndexA = indexA > -1 ? indexA : 9999;
                                        const safeIndexB = indexB > -1 ? indexB : 9999;

                                        return safeIndexA - safeIndexB;
                                    })[0];
                                } else {
                                    scope.recommendedPlaylist = [...recommendablePlaylists].sort(
                                        (a, b) => b.percentComplete - a.percentComplete,
                                    )[0];
                                }
                            }
                        }, 50);
                    },
                );

                scope.activatePlaylist = (playlist: PlaylistIguanaObject) => {
                    if (playlist && scope.activePlaylist && playlist.id === scope.activePlaylist.id) {
                        scope.switchLearningBoxMode('active_playlist');
                        return;
                    }

                    scope.activePlaylist = playlist;
                    scope.prefUps.activePlaylistLocalePackId = playlist.localePackId;
                    scope.currentUser.active_playlist_locale_pack_id = playlist.localePackId;

                    // sometimes we log in as users and change their active playlist
                    // for them, thus the lack of checking for ghostMode
                    scope.currentUser.save();

                    scope.switchLearningBoxMode('active_playlist');
                    scope.readyToShowPlaylistMap = false;
                };

                // Watch for the user's active playlist to change in push messages, which
                // can happen if they are accepted into a cohort. See https://trello.com/c/CH2RajXg
                scope.$watch('prefUps.activePlaylistLocalePackId', newVal => {
                    if (newVal && scope.playlists) {
                        updateActivePlaylistForLocalePackId();
                    }
                });

                // If the user has a relevant cohort, then the courses shown here are coming
                // from that cohort, so it makes sense to use that as the branding.  If the
                // user does not have a relevant cohort, this will fall back to the user's
                // branding, or if there is no user then the domain's.
                const brandingOpts = { branding: () => scope.relevantCohort?.branding };
                setupBrandNameProperties($injector, scope, brandingOpts);
                setupStyleHelpers($injector, scope, brandingOpts);
            },
        };
    },
]);
